<template>
  <div v-if="cards.length" class="stickerStripWrap">
    <div class="stickerStrip__trayLine">
      <span>{{ trayLine }}</span>
      <button
        v-if="canSetTray"
        type="button"
        class="stickerStrip__trayPick"
        :disabled="replacing"
        @click="$emit('choose-tray', selectedStickerIndex)"
      >
        设为托盘
      </button>
    </div>
    <div class="stickerStrip">
    <button type="button" class="stickerStrip__nav" aria-label="向左" @click="scrollByCard(-1)">‹</button>
    <div
      ref="scroller"
      class="stickerStrip__scroller"
      @wheel="onWheel"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div
        v-for="(item, index) in cards"
        :key="item.fileName || index"
        class="stickerStrip__card"
        :class="{
          'stickerStrip__card--selected': isSelected(item),
          'stickerStrip__card--bad': !item.qualified,
        }"
        :data-index="index"
        @dblclick.prevent="selectSticker(item)"
      >
        <div
          v-if="item.stickerIndex != null"
          class="stickerStrip__order"
          :class="{
            'stickerStrip__order--handle': replaceable,
            'stickerStrip__order--tray': item.tray,
          }"
        >
          {{ item.stickerIndex + 1 }}
        </div>
        <div v-else class="stickerStrip__order stickerStrip__order--spacer" />
        <div v-if="!item.url" class="stickerStrip__thumb stickerStrip__thumb--empty">无图</div>
        <div v-else-if="!isShown(index)" class="stickerStrip__thumb" />
        <img
          v-else
          class="stickerStrip__thumb"
          :src="item.url"
          alt=""
          draggable="false"
          decoding="async"
          :loading="index < 4 ? 'eager' : 'lazy'"
          :fetchpriority="index < 4 ? 'high' : 'low'"
          @load="rememberSize(item, $event)"
        >
        <div class="stickerStrip__emoji">
          {{ emojiText(item) || '未选 emoji' }}
        </div>
        <div v-if="item.tags && item.tags.length" class="stickerStrip__tags">
          <span
            v-for="tag in visibleTags(item)"
            :key="tag.tagKey || tag.name"
            class="stickerStrip__tag"
            :title="tagText(tag)"
          >{{ tag.name }}</span>
          <button
            v-if="overflowTagCount(item) > 0 && !isTagsExpanded(item)"
            type="button"
            class="stickerStrip__tag stickerStrip__tag--more"
            :title="overflowTagTitle(item)"
            @click.stop="expandTags(item)"
          >+{{ overflowTagCount(item) }}</button>
          <button
            v-if="isTagsExpanded(item) && item.tags.length > tagCapsuleLimit"
            type="button"
            class="stickerStrip__tag stickerStrip__tag--more"
            @click.stop="collapseTags(item)"
          >收起</button>
        </div>
        <div v-if="!item.qualified" class="stickerStrip__reason">{{ item.reason || '不合格' }}</div>
        <div v-if="sizeWarning(item)" class="stickerStrip__warning">{{ sizeWarning(item) }}</div>
        <div v-if="replaceable" class="stickerStrip__actions">
          <label
            class="stickerStrip__replace"
            :class="{ 'stickerStrip__replace--disabled': replacing }"
          >
            替换
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.gif"
              :disabled="replacing"
              @change="$emit('replace', item.fileName, $event)"
            >
          </label>
        </div>
      </div>
    </div>
    <button type="button" class="stickerStrip__nav" aria-label="向右" @click="scrollByCard(1)">›</button>
    </div>
    <p class="stickerStrip__count">共 {{ cards.length }} 张</p>
    <div class="stickerEmojiRow">
      <p class="stickerEmojiRow__hint">
        {{ selectedStickerIndex == null ? '双击一张贴纸，给它选 emoji，也可以把它设为托盘。' : (replaceable ? '再点一下已选中的 emoji 会取消。最多 3 个。' : '这一张贴纸已选的 emoji') }}
      </p>
      <div class="stickerEmojiRow__board">
        <button
          v-for="emoji in palette"
          :key="emoji"
          type="button"
          class="stickerEmojiRow__item"
          :class="{ 'stickerEmojiRow__item--on': selectedEmojis.indexOf(emoji) >= 0 }"
          :disabled="selectedStickerIndex == null || !replaceable"
          @click="onEmojiClick(emoji)"
        >
          {{ emoji }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import { STICKER_EMOJI_PALETTE, toggleStickerEmoji } from '../stickerEmoji'
import { stickerSizeWarning } from '../stickerExtaCards'

function wheelPixels(event, delta) {
  if (event.deltaMode === 1) {
    return delta * 16
  }
  if (event.deltaMode === 2) {
    return delta * window.innerHeight
  }
  return delta
}

// 松手前约 100 毫秒的平均速度。停住再松手就不甩。吸附会在惯性末尾把位置拽到格子上，那一下是生硬的，所以这里只做连续减速。
function releaseVelocity(samples, now) {
  const recent = samples.filter((sample) => now - sample.t <= 100)
  if (recent.length < 2) {
    return 0
  }
  const first = recent[0]
  const last = recent[recent.length - 1]
  const dt = last.t - first.t
  if (dt < 16 || now - last.t > 50) {
    return 0
  }
  const speed = (first.x - last.x) / dt
  const cap = 1.6
  return Math.max(-cap, Math.min(cap, speed))
}

function createStripPan(blocked) {
  let pointer = null
  let frame = 0
  let activeScroller = null

  function stopGlide() {
    if (frame) {
      cancelAnimationFrame(frame)
      frame = 0
    }
  }

  function clearPanning() {
    if (activeScroller) {
      activeScroller.classList.remove('is-panning')
    }
  }

  function glide(scroller, pxPerMs) {
    stopGlide()
    if (Math.abs(pxPerMs) < 0.02) {
      scroller.classList.remove('is-panning')
      return
    }
    let last = performance.now()
    let velocity = pxPerMs
    const step = (now) => {
      const dt = Math.min(34, now - last)
      last = now
      const before = scroller.scrollLeft
      scroller.scrollLeft = before + velocity * dt
      if (scroller.scrollLeft === before) {
        scroller.classList.remove('is-panning')
        frame = 0
        return
      }
      velocity *= Math.exp(-dt / 280)
      if (Math.abs(velocity) > 0.008) {
        frame = requestAnimationFrame(step)
        return
      }
      scroller.classList.remove('is-panning')
      frame = 0
    }
    frame = requestAnimationFrame(step)
  }

  return {
    down(event, scroller) {
      if (!scroller || event.button !== 0 || blocked(event.target)) {
        return
      }
      stopGlide()
      clearPanning()
      activeScroller = scroller
      pointer = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        left: scroller.scrollLeft,
        panning: false,
        scroller,
        samples: [{ x: event.clientX, t: event.timeStamp }],
      }
    },
    move(event) {
      if (!pointer || event.pointerId !== pointer.id) {
        return
      }
      const scroller = pointer.scroller
      const dx = event.clientX - pointer.x
      const dy = event.clientY - pointer.y
      if (!pointer.panning) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) {
          return
        }
        if (Math.abs(dx) <= Math.abs(dy)) {
          pointer = null
          return
        }
        pointer.panning = true
        pointer.x = event.clientX
        pointer.left = scroller.scrollLeft
        pointer.samples = [{ x: event.clientX, t: event.timeStamp }]
        scroller.classList.add('is-panning')
        if (scroller.setPointerCapture) {
          scroller.setPointerCapture(event.pointerId)
        }
      }
      pointer.samples.push({ x: event.clientX, t: event.timeStamp })
      const oldest = event.timeStamp - 120
      pointer.samples = pointer.samples.filter((sample) => sample.t >= oldest)
      scroller.scrollLeft = pointer.left - (event.clientX - pointer.x)
      event.preventDefault()
    },
    up(event) {
      if (!pointer || (event && event.pointerId !== pointer.id)) {
        return false
      }
      const scroller = pointer.scroller
      const panning = pointer.panning
      const velocity = releaseVelocity(pointer.samples, event ? event.timeStamp : performance.now())
      pointer = null
      if (!panning) {
        scroller.classList.remove('is-panning')
        return false
      }
      glide(scroller, velocity)
      return true
    },
    destroy() {
      stopGlide()
      clearPanning()
      pointer = null
    },
  }
}

function scrollParent(node) {
  let current = node.parentElement
  while (current) {
    const overflowY = window.getComputedStyle(current).overflowY
    const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'
    if (canScroll && current.scrollHeight > current.clientHeight + 1) {
      return current
    }
    current = current.parentElement
  }
  return document.scrollingElement || document.documentElement
}

export default {
  name: 'StickerStrip',
  props: {
    cards: { type: Array, default: () => [] },
    replaceable: { type: Boolean, default: false },
    replacing: { type: Boolean, default: false },
  },
  data() {
    return {
      shown: {},
      observer: null,
      palette: STICKER_EMOJI_PALETTE,
      selectedStickerIndex: null,
      tagCapsuleLimit: 3,
      expandedTagKeys: {},
      naturalSizes: {},
    }
  },
  watch: {
    cards(next, previous) {
      const countChanged = !previous || next.length !== previous.length
      const stillThere = this.cards.some((card) => card.stickerIndex === this.selectedStickerIndex)
      if (!stillThere) {
        this.selectedStickerIndex = null
      }
      // 轮询和排序会重建卡片；按稳定身份保留展开状态，只移除已经离开的贴纸。
      const present = new Set(this.cards.map((card) => this.tagKey(card)))
      Object.keys(this.expandedTagKeys).forEach((key) => {
        if (!present.has(key)) this.$delete(this.expandedTagKeys, key)
      })
      this.markFirstScreen()
      this.$nextTick(() => {
        this.watchOffscreen()
        // Sortable 按 DOM 现取子节点，同样张数只是内容变了不用重建；重建会 destroy 掉进行中的拖拽。
        if (countChanged) this.initSortable()
      })
    },
    replaceable() {
      this.$nextTick(this.initSortable)
    },
  },
  computed: {
    selectedEmojis() {
      const card = this.cards.find((item) => item.stickerIndex === this.selectedStickerIndex)
      return card && Array.isArray(card.emojis) ? card.emojis : []
    },
    trayLine() {
      const index = this.cards.findIndex((card) => card.tray)
      if (index < 0) {
        return '托盘：待选择'
      }
      return `托盘：第 ${index + 1} 张`
    },
    canSetTray() {
      if (!this.replaceable || this.selectedStickerIndex == null) {
        return false
      }
      const card = this.cards.find((item) => item.stickerIndex === this.selectedStickerIndex)
      return !!card && !!card.id && !card.tray
    },
  },
  created() {
    this.stripPan = createStripPan((target) => !!(target && target.closest && target.closest('button, a, input, label, .stickerStrip__order')))
  },
  mounted() {
    this.markFirstScreen()
    this.$nextTick(() => {
      this.watchOffscreen()
      this.initSortable()
    })
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
    // destroy 会同步触发 onEnd，先清掉待重建标记，免得卸载后又建一个
    this.sortableRebuildPending = false
    this.destroySortable()
    if (this.stripPan) {
      this.stripPan.destroy()
    }
  },
  methods: {
    /** 按地址记宽高：换图后地址变了，旧图的宽高不会套到新图上。 */
    rememberSize(item, event) {
      const img = event && event.target
      if (!item || !item.url || !img) return
      this.$set(this.naturalSizes, item.url, { width: img.naturalWidth, height: img.naturalHeight })
    },
    sizeWarning(item) {
      const size = item && this.naturalSizes[item.url]
      return size ? stickerSizeWarning(size.width, size.height) : ''
    },
    tagKey(item) {
      if (!item) return ''
      return item.id || item.fileName || String(item.stickerIndex)
    },
    tagText(tag) {
      const born = tag && tag.origin === 2 ? '模型创建' : '人工创建'
      const bound = tag && tag.binder === 2 ? '模型绑上' : '人绑上'
      return `${tag.name} · ${born} · ${bound}`
    },
    visibleTags(item) {
      const list = (item && item.tags) || []
      if (this.isTagsExpanded(item)) return list
      return list.slice(0, this.tagCapsuleLimit)
    },
    overflowTagCount(item) {
      const list = (item && item.tags) || []
      return Math.max(0, list.length - this.tagCapsuleLimit)
    },
    overflowTagTitle(item) {
      const list = (item && item.tags) || []
      return list.slice(this.tagCapsuleLimit).map((tag) => tag.name).join('、')
    },
    isTagsExpanded(item) {
      return !!this.expandedTagKeys[this.tagKey(item)]
    },
    expandTags(item) {
      this.$set(this.expandedTagKeys, this.tagKey(item), true)
    },
    collapseTags(item) {
      this.$delete(this.expandedTagKeys, this.tagKey(item))
    },
    emojiText(item) {
      const list = item && item.emojis
      if (!list || !list.length) {
        return ''
      }
      return list.join(' ')
    },
    isSelected(item) {
      return !!item && item.stickerIndex != null && item.stickerIndex === this.selectedStickerIndex
    },
    selectSticker(item) {
      if (!item || item.stickerIndex == null) {
        return
      }
      this.selectedStickerIndex = item.stickerIndex
      // 双击会让浏览器把图片当成选中内容，画面上盖一层蓝。选中范围清掉，照片保持原样。
      const selection = window.getSelection && window.getSelection()
      if (selection && selection.removeAllRanges) {
        selection.removeAllRanges()
      }
    },
    onEmojiClick(emoji) {
      if (this.selectedStickerIndex == null || !this.replaceable) {
        return
      }
      const card = this.cards.find((item) => item.stickerIndex === this.selectedStickerIndex)
      if (!card) {
        return
      }
      const next = toggleStickerEmoji(card.emojis, emoji)
      const previous = Array.isArray(card.emojis) ? card.emojis : []
      const unchanged = next.length === previous.length && next.every((item, index) => item === previous[index])
      if (unchanged) {
        return
      }
      this.$set(card, 'emojis', next)
      this.$emit('emoji', card, next)
    },
    isShown(index) {
      return !!this.shown[index]
    },
    // 眼前 4 张先下载。其余等滑进这一行再下，避免打开页面时整包图一起抢带宽。
    markFirstScreen() {
      const count = Math.min(4, this.cards.length)
      for (let index = 0; index < count; index += 1) {
        if (!this.shown[index]) {
          this.$set(this.shown, index, true)
        }
      }
    },
    watchOffscreen() {
      if (this.observer) {
        this.observer.disconnect()
      }
      const root = this.$refs.scroller
      if (!root || typeof IntersectionObserver === 'undefined') {
        const shown = {}
        this.cards.forEach((_, index) => {
          shown[index] = true
        })
        this.shown = shown
        return
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return
          }
          const index = Number(entry.target.getAttribute('data-index'))
          if (!this.shown[index]) {
            this.$set(this.shown, index, true)
          }
        })
      }, { root, rootMargin: '80px', threshold: 0.01 })
      root.querySelectorAll('[data-index]').forEach((node) => {
        this.observer.observe(node)
      })
    },
    scrollByCard(direction) {
      const scroller = this.$refs.scroller
      if (!scroller) {
        return
      }
      const card = scroller.querySelector('.stickerStrip__card')
      const gap = 12
      const width = card ? card.getBoundingClientRect().width + gap : scroller.clientWidth / 4
      scroller.scrollBy({ left: direction * width, behavior: 'smooth' })
    },
    // 横向交给浏览器自己滑，带惯性。竖向仍还给页面，避免这一行把下滑吃掉。
    onWheel(event) {
      this.steerWheel(event, this.$refs.scroller)
    },
    steerWheel(event, scroller) {
      if (!scroller) {
        return
      }
      const deltaX = wheelPixels(event, event.deltaX)
      const deltaY = wheelPixels(event, event.deltaY)
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        return
      }
      const parent = scrollParent(scroller)
      parent.scrollTop += deltaY
      event.preventDefault()
    },
    onPointerDown(event) {
      this.stripPan.down(event, this.$refs.scroller)
    },
    onPointerMove(event) {
      this.stripPan.move(event)
    },
    onPointerUp(event) {
      this.stripPan.up(event)
    },
    initSortable() {
      if (this.dragging) {
        this.sortableRebuildPending = true
        return
      }
      this.destroySortable()
      const el = this.$refs.scroller
      if (!el || !this.replaceable || this.cards.length < 2) {
        return
      }
      this.sortable = Sortable.create(el, {
        handle: '.stickerStrip__order--handle',
        draggable: '.stickerStrip__card',
        animation: 180,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        direction: 'horizontal',
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 3,
        scroll: el,
        scrollSensitivity: 70,
        scrollSpeed: 14,
        ghostClass: 'stickerStrip__card--ghost',
        dragClass: 'stickerStrip__card--drag',
        onStart: (evt) => {
          this.dragging = true
          this.stripPan.destroy()
          this.dragOriginalNextSibling = evt.item.nextSibling
        },
        onEnd: (evt) => {
          this.dragging = false
          if (this.sortableRebuildPending) {
            this.sortableRebuildPending = false
            this.$nextTick(this.initSortable)
          }
          const parent = evt.from
          const item = evt.item
          const originalNext = this.dragOriginalNextSibling
          this.dragOriginalNextSibling = null
          if (parent) {
            if (originalNext && originalNext.parentNode === parent) {
              parent.insertBefore(item, originalNext)
            } else {
              parent.appendChild(item)
            }
          }
          const fromCard = this.cards[evt.oldIndex]
          const toCard = this.cards[evt.newIndex]
          if (!fromCard || !toCard || fromCard.stickerIndex == null || toCard.stickerIndex == null) {
            return
          }
          if (evt.oldIndex === evt.newIndex) {
            return
          }
          this.$emit('reorder', fromCard.stickerIndex, toCard.stickerIndex)
        },
      })
    },
    destroySortable() {
      if (this.sortable) {
        this.sortable.destroy()
        this.sortable = null
      }
    },
  },
}
</script>

<style scoped>
.stickerStripWrap {
  margin-top: 28px;
}
.stickerStrip__trayLine {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 0 10px;
  color: #57534e;
  font-size: 13px;
}
.stickerStrip {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stickerStrip__count {
  margin: 14px 0 0;
  text-align: center;
  color: #57534e;
  font-size: 13px;
}
.stickerStrip__nav {
  flex: 0 0 28px;
  height: 28px;
  border: 1px solid #d6d3d1;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
}
.stickerStrip__order {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0 auto 8px;
  border: 1px solid #d6d3d1;
  border-radius: 14px;
  background: #fff;
  color: #44403c;
  font-size: 13px;
  line-height: 1;
  user-select: none;
}
.stickerStrip__order--spacer {
  visibility: hidden;
}
.stickerStrip__order--tray {
  background: var(--link-accent);
  border-color: var(--link-accent);
  color: #fff;
}
.stickerStrip__order--handle {
  cursor: grab;
}
.stickerStrip__order--handle:active {
  cursor: grabbing;
}
.stickerStrip__scroller {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  touch-action: pan-y;
  cursor: grab;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.stickerStrip__scroller.is-panning {
  cursor: grabbing;
  user-select: none;
}
.stickerStrip__scroller::-webkit-scrollbar {
  display: none;
  height: 0;
}
.stickerStrip__card {
  flex: 0 0 calc((100% - 36px) / 4);
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 6px 10px;
  border-radius: 12px;
  background-color: transparent;
  text-align: center;
  transition: background-color 0.16s ease;
  user-select: none;
}
/* 选中只换底色。外描边会画进卡片之间的缝，看起来像左右两条线。 */
.stickerStrip__card--selected {
  background-color: color-mix(in srgb, var(--link-accent) 16%, #fff);
}
.stickerStrip__card--bad {
  background-color: color-mix(in srgb, #b91c1c 14%, #fff);
}
.stickerStrip__card--bad.stickerStrip__card--selected {
  background-color: color-mix(in srgb, #b91c1c 24%, #fff);
}
.stickerStrip__card--ghost {
  opacity: 0.4;
}
.stickerStrip__card--drag {
  opacity: 0.95;
}
.stickerStrip__thumb {
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  display: block;
  /* 双击时浏览器会把图片当成选中内容。事件不落在照片上，画面就不会盖一层蓝。 */
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}
.stickerStrip__thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #78716c;
}
.stickerStrip__emoji {
  margin-top: 4px;
  min-height: 18px;
  font-size: 16px;
  line-height: 1.2;
  color: #44403c;
}
.stickerStrip__tags {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
}
.stickerStrip__tag {
  display: inline-flex;
  max-width: 100%;
  box-sizing: border-box;
  padding: 1px 6px;
  border: 0;
  border-radius: 999px;
  background: #f5f5f4;
  color: #57534e;
  font-size: 11px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stickerStrip__tag--more {
  cursor: pointer;
  background: #e7e5e4;
  color: #44403c;
}
.stickerStrip__tag--more:hover {
  background: #d6d3d1;
}
.stickerEmojiRow {
  margin-top: 12px;
}
.stickerEmojiRow__hint {
  margin: 0 0 8px;
  color: #57534e;
  font-size: 12px;
}
.stickerEmojiRow__board {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.stickerEmojiRow__item {
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  font-size: 22px;
  line-height: 36px;
  cursor: pointer;
}
.stickerEmojiRow__item--on {
  border-color: #b45309;
  background: #fff7ed;
}
.stickerEmojiRow__item:disabled {
  cursor: default;
}
.stickerStrip__warning {
  margin-top: 4px;
  color: #b45309;
  font-size: 11px;
  line-height: 1.4;
}
.stickerStrip__reason {
  margin-top: 4px;
  color: #9f1239;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  overflow-wrap: anywhere;
}
.stickerStrip__actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
}
.stickerStrip__trayPick,
.stickerStrip__replace {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;
  padding: 0 12px;
  height: 24px;
  border: 1px solid #d6d3d1;
  border-radius: 999px;
  background: #fff;
  color: #44403c;
  font-size: 12px;
  line-height: 24px;
  cursor: pointer;
}
.stickerStrip__trayPick:hover:not(:disabled),
.stickerStrip__replace:hover {
  border-color: #a8a29e;
  color: #1c1917;
}
.stickerStrip__trayPick:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.stickerStrip__replace--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.stickerStrip__replace input {
  display: none;
}
</style>
